import React, { useState } from "react";
import { Button, Input, Modal, notification, Select } from "antd";
import {
    useDeleteDashboardMutation,
    useSaveDashboardMutation, useUpdateDashboardMutation,
} from "../../store/api/lumi/dashboard";
import { formatDataForAntdDropdown } from "../../utils/formatDataForAntdDropdown";
import { MdAdd, MdOutlineDelete } from "react-icons/md";
import { v4 as uuidv4 } from "uuid";
import { FiSave } from "react-icons/fi";
import EditInputElement from "../Common/EditInputElement";
import { useNavigate, useParams } from "react-router-dom";
import TopFilters from "./TopFilters/TopFilters";
import { FilterFilled } from "@ant-design/icons";
import { ConceptType } from "../../constants";
import { useSelector } from "react-redux";
import { selectDashboardById } from "../../store/selectors/selectors";

const DashboardManager = ({
                              dashboardList,
                              onDashboardSelected,
                              currentDashboard,
                              widgets,
                              setCurrentDashboard,
                              metaData,
                              dataList,
                              showFilters,
                              setShowFilters,
                          }) => {

    const author = localStorage.getItem("username");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [dashboardName, setDashboardName] = useState("");
    const [dashboardDescription, setDashboardDescription] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const [saveDashboard] = useSaveDashboardMutation();
    const [deleteDashboard] = useDeleteDashboardMutation();
    const [updateDashboard] = useUpdateDashboardMutation();

    const { id } = useParams();

    const {
        topFilters = [],
        selections = {},
        topConcepts = [],
        activeConceptList = "",
    } = useSelector((state) => selectDashboardById(state, id));

    const navigate = useNavigate();
    const isNameUnique = (name) => {
        return !dashboardList?.some(
            (dashboard) => dashboard.name.toLowerCase() === name.toLowerCase(),
        );
    };

    const checkAuth = () => {
        return author === currentDashboard.author;
    };

    const updateWidgetsIds = (charts) => {
        return charts.map((chart) => ({
            ...chart,
            id: uuidv4(),
        }));
    };

    const handleUpdateDashboard = async (name, description) => {
        if (!currentDashboard) {
            notification.error({
                message: "Error Updating Dashboard",
                description: "No dashboard is currently selected for updating.",
            });
            return;
        }

        try {
            const isNameString = typeof name === "string";
            const isDescriptionString = typeof description === "string";
            const updatedWidgetsIds = updateWidgetsIds(widgets);
            const updatedDashboard = {
                ...currentDashboard,
                name: isNameString ? name.trim() : currentDashboard.name,
                description: isDescriptionString ? description : currentDashboard.description,
                charts: updatedWidgetsIds,
                top_filters: topFilters,
                selections: selections,
                top_concepts: topConcepts,
                active_concept_list: activeConceptList || ConceptType.Top,
            };

            const result = await updateDashboard({
                projectId: id,
                dashboardId: currentDashboard.dashboard_id,
                data: updatedDashboard,
            }).unwrap();
            setCurrentDashboard(result);
            navigate(`/projects/${id}/dashboard/${result.name}`);
            notification.success({
                message: "Dashboard Updated!",
                description: "Your dashboard has been updated successfully.",
            });
        } catch (error) {
            notification.error({
                message: "Error Updating Dashboard",
                description: "There was an error updating your dashboard: " + error.message,
            });
        }
    };

    const handleSaveDashboard = async () => {
        if (isLoading) return;
        setIsLoading(true);

        if (!dashboardName || !isNameUnique(dashboardName)) {
            setIsLoading(false);
            notification.error({
                message: "Error Saving Dashboard",
                description: !dashboardName
                    ? "Please provide a dashboard name."
                    : "Dashboard name already exists. Please use a different name.",
            });
            return;
        }
        const updatedWidgetsIds = updateWidgetsIds(widgets);
        const dashboardData = {
            name: dashboardName,
            description: dashboardDescription,
            charts: updatedWidgetsIds,
            top_filters: topFilters,
            selections: selections,
            top_concepts: topConcepts,
            active_concept_list: activeConceptList || ConceptType.Top,
        };

        try {
            const result = await saveDashboard({
                id: id,
                dashboard: dashboardData,
            }).unwrap();
            notification.success({
                message: "Dashboard Saved!",
                description: "Your dashboard has been saved successfully.",
            });
            setCurrentDashboard(result);
            navigate(`/projects/${id}/dashboard/${result.name}`);
            setIsModalOpen(false);
            setDashboardName("");
            setDashboardDescription("");
        } catch (error) {
            notification.error({
                message: "Error Saving Dashboard",
                description: "There was an error saving your dashboard.",
            });
        } finally {
            setIsLoading(false);
        }
    };
    const onDeleteDashboard = async () => {
        if (!currentDashboard) {
            notification.error({
                message: "Error Deleting Dashboard",
                description: "No dashboard is currently selected for deletion.",
            });
            return;
        }

        try {
            await deleteDashboard({
                projectId: id,
                dashboardId: currentDashboard.dashboard_id,
            }).unwrap();
            notification.success({
                message: "Dashboard Deleted",
                description: "The dashboard has been successfully deleted.",
            });

            navigate(`/projects/${id}/dashboard`);
            setCurrentDashboard(null);
        } catch (error) {
            notification.error({
                message: "Error Deleting Dashboard",
                description: "There was an error deleting the dashboard.",
            });
        }
    };

    const handleKeyPress = (e, submitFunction) => {
        if (e.key === "Enter") {
            submitFunction();
        }
    };

    return (
        <div>
            <div className="header-right-section dashboard-header">
                <Modal
                    title="Save Dashboard"
                    open={isModalOpen}
                    onOk={handleSaveDashboard}
                    onCancel={() => setIsModalOpen(false)}
                >
                    <div className={"modal-inputs-edit"}>
                        <Input
                            placeholder="Dashboard Name"
                            value={dashboardName}
                            onChange={(e) => setDashboardName(e.target.value)}
                            onKeyPress={(e) =>
                                handleKeyPress(e, handleSaveDashboard)
                            }
                        />
                        <Input
                            placeholder="Dashboard Description"
                            value={dashboardDescription}
                            onChange={(e) =>
                                setDashboardDescription(e.target.value)
                            }
                            onKeyPress={(e) =>
                                handleKeyPress(e, handleSaveDashboard)
                            }
                        />
                    </div>
                </Modal>

                {currentDashboard && (
                    <div className={"header-dashboard-name"}>
                        <EditInputElement
                            style={{ fontWeight: "bold", fontSize: "16px" }}
                            initialValue={currentDashboard.name}
                            onSave={(value) =>
                                handleUpdateDashboard(value, null)
                            }
                            placeholder="Enter Dashboard Name"
                            isUnique={isNameUnique}
                            isAuthor={checkAuth()}
                        />
                        <EditInputElement
                            style={{ fontStyle: "italic", fontSize: "16px" }}
                            initialValue={currentDashboard.description}
                            onSave={(value) =>
                                handleUpdateDashboard(null, value)
                            }
                            placeholder="Enter Dashboard Description"
                            isAuthor={checkAuth()}
                        />
                    </div>
                )}
                <div className="header-options-container">
                    <Button
                        data-testid="filterButton"
                        style={
                            showFilters
                                ? {
                                    color: "#4096ff",
                                    borderColor: "#4096ff",
                                }
                                : {}
                        }
                        icon={<FilterFilled />}
                        onClick={() =>
                            setShowFilters((prevState) => !prevState)
                        }
                    ></Button>

                    <div className="header-other-options">
                        <Select
                            allowClear={true}
                            showSearch
                            placeholder="Select dashboard"
                            optionFilterProp="children"
                            style={{ width: 200 }}
                            onChange={onDashboardSelected}
                            value={currentDashboard?.name}
                            filterOption={(input, option) =>
                                option?.label
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                            }
                            options={
                                dashboardList && dashboardList.length > 0
                                    ? formatDataForAntdDropdown(dashboardList)
                                    : []
                            }
                        />
                        {!currentDashboard && (
                            <Button
                                className="save-dashboard-button"
                                icon={
                                    <MdAdd className="button-icon font-size-20" />
                                }
                                onClick={() => setIsModalOpen(true)}
                            ></Button>
                        )}
                        {currentDashboard && checkAuth() && (
                            <>
                                <Button
                                    className="save-dashboard-button"
                                    icon={
                                        <FiSave className="button-icon font-size-20" />
                                    }
                                    onClick={handleUpdateDashboard}
                                ></Button>
                                <Button
                                    className="save-dashboard-button"
                                    icon={
                                        <MdOutlineDelete className="button-icon font-size-20" />
                                    }
                                    onClick={onDeleteDashboard}
                                ></Button>
                            </>
                        )}
                    </div>
                </div>
            </div>
            <TopFilters
                isOpen={showFilters}
                metadata={metaData}
                dataList={dataList}
            />
        </div>
    );
};

export default DashboardManager;
